import React, { useState } from 'react';

import { createList } from '@vlabs/api/luna-api/client';
import { CrossIcon } from '@vlabs/icons';
import { selectListOptions } from '@vlabs/shared/selectors/listSelectors';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import type { TOption, WithDataTestid } from '@vlabs/uikit/types';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AddCardsToListForm.module.sass';
import { useAddCardsToListFormTranslation } from './i18n';

interface TAddCardsToListFormValues {
  listId: string | TOption;
}

interface AddCardsToListFormProps extends WithDataTestid {
  onSubmit: (listId: string) => Promise<void>;
  onCancel: VoidFunction;
}

export function AddCardsToListForm({
  'data-testid': dataTestId = 'AddCardsToListForm',
  onSubmit,
  onCancel,
}: AddCardsToListFormProps): React.JSX.Element {
  const { t } = useAddCardsToListFormTranslation();
  const lists = useSelector(selectListOptions);

  const [mode, setMode] = useState<'select' | 'create'>('select');

  const {
    control,
    register,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<TAddCardsToListFormValues>({ defaultValues: { listId: '' } });

  const listValue = watch('listId');
  const testid = buildTestid(dataTestId);

  const submitForm = async ({ listId }: TAddCardsToListFormValues) => {
    if (mode === 'create') {
      if (typeof listId !== 'string' || !listId) {
        return;
      }

      try {
        const createResponse = await createList({ user_data: listId });

        await onSubmit(createResponse.list_id);
      } catch {
        toast.error(t('toast_create_list_error'));
      }

      return;
    }

    if (listId && typeof listId !== 'string') {
      await onSubmit(listId.value);
    }
  };

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <div className={st.Root}>
        <span className={st.Title}>{t('title', { context: 'add' })}</span>

        <div className={st.ListsOptions}>
          <Controller
            control={control}
            name="listId"
            render={({ field }) => (
              <Control.Select
                key={mode}
                {...field}
                disabled={mode === 'create'}
                id="lists"
                options={lists}
                placeholder={t('select_placeholder')}
                value={typeof field.value === 'string' ? undefined : field.value}
                {...testid('ExistingListsSelect')}
              />
            )}
          />
          {mode === 'select' && (
            <Control.Button
              className={st.CreateListButton}
              kind="primary"
              onClick={() => {
                setMode('create');
                reset({ listId: '' });
              }}
              variant="flat"
              {...testid('CreateListSwitchModeButton')}
            >
              {t('new_list_button')}
            </Control.Button>
          )}
          {mode === 'create' && (
            <div className={st.CreateNewList}>
              <div className={st.InputWrapper}>
                <Control.Input
                  {...register('listId', { maxLength: validate.maxLength(128) })}
                  errors={errors}
                  id="newListName"
                  label={t('input_label')}
                  placeholder={t('input_placeholder')}
                  {...testid('CreateNewListInput')}
                />
              </div>
              <Control.IconButton
                className={st.IconButton}
                icon={<CrossIcon />}
                kind="negative"
                onClick={() => {
                  setMode('select');
                  reset({ listId: '' });
                }}
                variant="flat"
                {...testid('SwitchModeButton')}
              />
            </div>
          )}
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.Action}
            disabled={!listValue}
            kind="primary"
            type="submit"
            variant="filled"
            {...testid('SubmitButton')}
          >
            {t('submit_button', { context: 'add' })}
          </Control.Button>
          <Control.Button
            className={st.Action}
            kind="secondary"
            onClick={onCancel}
            variant="outlined"
            {...testid('CloseButton')}
          >
            {t('cancel_button')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}
